"use client";
import { ProgramTypeVariants, DeliveryStatus, WarehouseChannelBadgeStatus, StockVariants } from "../constants.js";
import { PRODUCT_CLASS_TYPES } from "../searchService/LucidWorks/constants.js";
import { isBD } from "../../utils/businessType.lib.js";
import { isValidUrl, hasMembershipId, hasActiveMembership } from "../../utils/index.js";
import { ATTRIBUTE_DELIVERY_TYPE, ATTRIBUTE_WAREHOUSE_PICK_UP_VALUE } from "./normalizePCP/constants.js";
const getProductNavigationUrl = (name, itemNumber, domainUrl, swatchName, label, enableBModPDPUrl) => {
  if (!name || !itemNumber) return null;
  const productName = name.toLowerCase().replace(/ /g, "-").replace(/[^A-Za-z0-9-]/g, "");
  if (!swatchName) {
    return enableBModPDPUrl ? `${domainUrl}p/-/${productName}/${itemNumber}` : `${domainUrl}${productName}.product.${itemNumber}.html`;
  }
  const searchParams = encodeURIComponent(
    `${label == null ? void 0 : label.toLocaleLowerCase()}:${swatchName.toLocaleLowerCase()}`
  );
  return enableBModPDPUrl ? `${domainUrl}p/-/${productName}/${itemNumber}?preselect=${searchParams}` : `${domainUrl}${productName}.product.${itemNumber}.html?preselect=${searchParams}`;
};
const getColorSwatchesData = (translationsConfig, imageConfig, item, childData, featureToggleConfig) => {
  var _a, _b;
  if (((_a = item.fieldData) == null ? void 0 : _a.swatchable) !== 1 || !childData || !((_b = childData.catalogData) == null ? void 0 : _b.length)) {
    return null;
  }
  const { domainUrl } = imageConfig || {};
  const { bfSwatchURL, extension } = (translationsConfig == null ? void 0 : translationsConfig.swatchControls) || {};
  const { description, itemNumber } = item;
  const colorSwatchesData = [];
  childData.catalogData.forEach((cItem) => {
    const { attributes } = cItem;
    attributes == null ? void 0 : attributes.forEach((aItem) => {
      var _a2;
      if ("swatchImage" in aItem && aItem["swatchImage"]) {
        const { swatchImage, value, key } = aItem;
        if (!(colorSwatchesData == null ? void 0 : colorSwatchesData.find((s) => s.id === swatchImage))) {
          colorSwatchesData == null ? void 0 : colorSwatchesData.push({
            attributeValue: value || "",
            id: swatchImage,
            label: key || "",
            pdpNavigationUrl: getProductNavigationUrl(
              (description == null ? void 0 : description.shortDescription) || "",
              itemNumber || "",
              domainUrl,
              swatchImage,
              key,
              (_a2 = featureToggleConfig == null ? void 0 : featureToggleConfig.enableBModPDPNavigationUrl) == null ? void 0 : _a2.isEnabled
            ),
            productImageUrl: getImageUrl(cItem, item),
            swatchImageUrl: `${bfSwatchURL}${swatchImage}${extension}`
          });
        }
      }
    });
  });
  return (colorSwatchesData == null ? void 0 : colorSwatchesData.length) ? colorSwatchesData : null;
};
const getImageUrl = (item, parentCatalogItem) => {
  var _a, _b, _c, _d;
  if (!((_a = item == null ? void 0 : item.fieldData) == null ? void 0 : _a.imageName) && !((_b = parentCatalogItem == null ? void 0 : parentCatalogItem.fieldData) == null ? void 0 : _b.imageName)) {
    return null;
  }
  const imageName = ((_c = item == null ? void 0 : item.fieldData) == null ? void 0 : _c.imageName) || ((_d = parentCatalogItem == null ? void 0 : parentCatalogItem.fieldData) == null ? void 0 : _d.imageName);
  const isBfldrImgUrl = isValidUrl(imageName);
  if (isBfldrImgUrl) {
    return imageName;
  }
  return null;
};
const getFsaEligibility = (childData) => {
  var _a, _b;
  if (!childData || !((_a = childData.catalogData) == null ? void 0 : _a.length)) return false;
  return (_b = childData == null ? void 0 : childData.catalogData) == null ? void 0 : _b.some(
    (item) => {
      var _a2;
      return ((_a2 = item.additionalFieldData) == null ? void 0 : _a2.fsa) === 1;
    }
  );
};
const getChdiEligibility = (childData) => {
  var _a, _b;
  if (!childData || !((_a = childData.catalogData) == null ? void 0 : _a.length)) return false;
  return (_b = childData == null ? void 0 : childData.catalogData) == null ? void 0 : _b.some(
    (item) => {
      var _a2;
      return ((_a2 = item.additionalFieldData) == null ? void 0 : _a2.chdIndicator) === 1;
    }
  );
};
const checkProgramTypeKeyExists = (programTypes, key) => {
  if (!programTypes) return false;
  return new Set(programTypes.toLowerCase().split(",")).has(
    key.toLowerCase()
  );
};
const getProductDeliveryBadgeData = (item, translationsConfig, baseUrl) => {
  const { urls } = translationsConfig || {};
  const { programTypes } = item;
  const checkTwoDayDeliveryExists = checkProgramTypeKeyExists(
    programTypes || "",
    ProgramTypeVariants.TWO_DAY_DELIVERY
  );
  const checkCostcoGroceryExists = checkProgramTypeKeyExists(
    programTypes || "",
    ProgramTypeVariants.COSTCOGROCERY
  );
  if (checkTwoDayDeliveryExists || checkCostcoGroceryExists) {
    return {
      linkUrl: `${baseUrl}${(urls == null ? void 0 : urls.groceryURL) || ""}`
    };
  }
  return null;
};
const getProductFeaturesData = (featuresData) => {
  if (featuresData) {
    const result = featuresData.split(";").map((str) => {
      return str.replace(/\n$/, "").trim();
    }).filter((str) => str);
    return result;
  }
  return null;
};
const getMemberOnlyData = (item, childData) => {
  var _a, _b;
  if (!childData || !((_a = childData.catalogData) == null ? void 0 : _a.length)) return false;
  const { additionalFieldData } = item;
  return (_b = childData == null ? void 0 : childData.catalogData) == null ? void 0 : _b.some(
    (childItem) => {
      var _a2;
      return ((_a2 = childItem.additionalFieldData) == null ? void 0 : _a2.membershipReqd) === 1 && (additionalFieldData == null ? void 0 : additionalFieldData.dispPriceInCartOnly) === 1;
    }
  );
};
const getReviewRatingData = (item, translationsObj) => {
  const { eligibleForReviews, rating, numberOfRating } = item.additionalFieldData || {};
  const { translations } = translationsObj || {};
  if (eligibleForReviews === 1 && typeof rating === "string") {
    const parsedRating = parseFloat(rating).toFixed(2);
    const mapObj = {
      "{rating}": parsedRating,
      "{total}": `${numberOfRating}`
    };
    const ariaLabelText = translations == null ? void 0 : translations.averageratingarialabel.replace(
      /{rating}|{total}/gi,
      (matched) => mapObj[matched]
    );
    return {
      ariaLabel: +parsedRating ? ariaLabelText : translations == null ? void 0 : translations.noreviewsarialabel,
      reviewCount: numberOfRating,
      value: +parsedRating
    };
  }
  return null;
};
const getInWarehouseAvailabilityText = (item) => {
  const { programTypes } = item;
  return Boolean(
    programTypes == null ? void 0 : programTypes.toLowerCase().includes(ProgramTypeVariants.TWO_DAY_DELIVERY.toLowerCase())
  );
};
const getReplacementItemNumber = (fulfillmentData) => {
  var _a;
  const fulfillmentItem = fulfillmentData == null ? void 0 : fulfillmentData.find(
    (item) => {
      const { field1Data } = item;
      return (field1Data == null ? void 0 : field1Data.replacementType) === "REPLACEMENT";
    }
  );
  if (!fulfillmentItem) return null;
  return (_a = fulfillmentItem == null ? void 0 : fulfillmentItem.field1Data) == null ? void 0 : _a.replacedItem;
};
const getPillBadges = (item, pillBadgeConfig, domainUrl, keyword) => {
  var _a, _b;
  if (!item.attributes || !item.attributes.length) return null;
  const { defaultColor, colorMap, catalogSearchUrl } = pillBadgeConfig || {};
  return (_b = (_a = item.attributes) == null ? void 0 : _a.filter((item2) => item2.pills === "1")) == null ? void 0 : _b.map((item2) => {
    var _a2, _b2;
    const searchParams = encodeURIComponent(
      ((_a2 = item2.value) == null ? void 0 : _a2.toLocaleLowerCase()) || ""
    );
    const pillBadgeLinkUrl = pillBadgeConfig.isDomainInclusionDisabledByKeyword && !keyword ? `?keyword=${searchParams}` : `${domainUrl}${catalogSearchUrl}${searchParams}`;
    const tranformedColorKey = ((_b2 = item2.value) == null ? void 0 : _b2.replace(/[\s]|[&/\\#,+()$~%.:*?<>{}]/g, "_")) || "";
    const color = (colorMap == null ? void 0 : colorMap[tranformedColorKey]) || defaultColor;
    return {
      color,
      link: pillBadgeLinkUrl.toString(),
      text: item2.value
    };
  });
};
const getAddToListButtonData = (item, productName, hasSingleSku, cookieContent, childData) => {
  const imageUrl = getImageUrl(item);
  const price = getPriceData(item, hasSingleSku, cookieContent, childData);
  const { additionalFieldData, buyable, attributes } = item;
  const { productClassType } = additionalFieldData || {};
  const isBundle = attributes == null ? void 0 : attributes.some((attr) => attr.key === "Bundle");
  if (hasSingleSku && buyable && !PRODUCT_CLASS_TYPES.includes(productClassType || "") && !isBundle) {
    return {
      image: imageUrl,
      name: productName,
      price: price == null ? void 0 : price.priceValue
    };
  }
  return null;
};
const getCompareProductButtonData = (item) => {
  const { programTypes, fieldData } = item;
  const { comparable } = fieldData || {};
  const isTwoDayDeliveryKeyExists = checkProgramTypeKeyExists(
    programTypes || "",
    ProgramTypeVariants.TWO_DAY_DELIVERY
  );
  return !isTwoDayDeliveryKeyExists && comparable === 1;
};
const getTotalDiscount = (discounts) => {
  if (!discounts || !discounts.length) return 0;
  return Math.floor(
    discounts.reduce((total, discount) => {
      const { promoAmount } = discount;
      const parsedPromoAmount = parseFloat(
        (promoAmount == null ? void 0 : promoAmount.toString()) || "0"
      );
      return total + (parsedPromoAmount > 0 ? parsedPromoAmount : 0);
    }, 0)
  );
};
const getDiscountsData = (childData) => {
  if (!childData || !childData.fulfillmentData || childData.fulfillmentData.length !== 1)
    return null;
  return {
    displayDiscount: isBD(),
    totalDiscount: getTotalDiscount(childData.fulfillmentData[0].discounts)
  };
};
const getPriceList = (childData, ecoFee) => {
  if (!childData) return [];
  const isBDProduct = isBD();
  const parsedIncludeInPriceEcoFee = (ecoFee == null ? void 0 : ecoFee.includeInPrice) ? parseFloat(ecoFee.includeInPrice) || 0 : 0;
  const extractPrices = (priceOptions, price, discounts) => {
    if (price) {
      const parsedPrice = parseFloat(price.toString());
      if (parsedPrice > 0) {
        const parsedPriceWithEcoFee = parsedPrice + parsedIncludeInPriceEcoFee;
        const totalDiscount = getTotalDiscount(discounts);
        const finalPrice = parsedPriceWithEcoFee - totalDiscount;
        priceOptions.push(finalPrice);
      }
    }
  };
  const getMinPrice = (priceOptions) => {
    return priceOptions.length > 0 ? Math.min(...priceOptions) : void 0;
  };
  const data = isBDProduct ? childData.fulfillmentData : childData.catalogData;
  if (!data) return [];
  return data.map((item) => {
    const priceOptions = [];
    if (isBDProduct) {
      const {
        listPrice = "",
        price = "",
        discounts
      } = item;
      extractPrices(priceOptions, listPrice ?? "", discounts);
      extractPrices(priceOptions, price ?? "", discounts);
    } else {
      const { priceData } = item;
      extractPrices(priceOptions, (priceData == null ? void 0 : priceData.listPrice) ?? "");
      extractPrices(priceOptions, (priceData == null ? void 0 : priceData.price) ?? "");
    }
    return getMinPrice(priceOptions);
  }).filter((value) => value !== void 0);
};
const getPriceData = (item, hasSingleSku, cookieContext, childData, ecoFee, signedIn, isProductDetailsPage) => {
  var _a, _b;
  const { wcMember } = cookieContext;
  if (!hasMembershipId(wcMember.value, signedIn, isProductDetailsPage) && getMemberOnlyData(item, childData))
    return null;
  const { programTypes, additionalFieldData, attributes } = item;
  const priceList = getPriceList(childData, ecoFee) || [];
  const { dispPriceInCartOnly } = additionalFieldData || {};
  const toLowerCaseProgramTypeSet = new Set(
    (_a = programTypes == null ? void 0 : programTypes.toLowerCase()) == null ? void 0 : _a.split(",")
  );
  const acceptedProgramTypes = [
    ProgramTypeVariants.LOCATION_CONTROLLED_INVENTORY,
    ProgramTypeVariants.TWO_DAY_DELIVERY,
    ProgramTypeVariants.SITE_CONTROLLED_INVENTORY,
    ProgramTypeVariants.STANDARD,
    ProgramTypeVariants.NATIONAL,
    ProgramTypeVariants.SAME_DAY_DELIVERY
  ];
  const programTypeCheck = isBD() ? true : acceptedProgramTypes.some(
    (type) => toLowerCaseProgramTypeSet == null ? void 0 : toLowerCaseProgramTypeSet.has(type.toLowerCase())
  );
  if (programTypeCheck && dispPriceInCartOnly !== 2 && priceList.length) {
    const hasPriceRangeAttr = ((_b = attributes == null ? void 0 : attributes.find((attr) => attr.key === "Hide_Price_Range")) == null ? void 0 : _b.value) === "1";
    const minSalePrice = Math.min(...priceList);
    const maxSalePrice = Math.max(...priceList);
    if (!hasSingleSku) {
      if (hasPriceRangeAttr && minSalePrice < maxSalePrice) {
        return {
          priceText: "Starting at",
          priceValue: minSalePrice
        };
      } else if (minSalePrice === maxSalePrice) {
        return {
          priceText: null,
          priceValue: minSalePrice
        };
      } else {
        return {
          priceText: null,
          priceValue: [minSalePrice, maxSalePrice]
        };
      }
    } else {
      return {
        priceText: null,
        priceValue: minSalePrice
      };
    }
  }
  return null;
};
const getProgramTypeAvailability = (programTypeObj) => {
  return (programTypeObj == null ? void 0 : programTypeObj.availability) === StockVariants.IN_STOCK || (programTypeObj == null ? void 0 : programTypeObj.availability) === StockVariants.LOW_STOCK;
};
const getInWareHouseChannelBadgeData = (item, enabledWarehousesListConfig, cookieContext, showMultiVariantsAvailability = false, hasSingleSku, inventoryData, publishedChildItems) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  const { warehouse, userLocation } = cookieContext;
  const { nearestWarehouse } = warehouse;
  const { city } = userLocation;
  const isWarehouseEnabled = (_a = enabledWarehousesListConfig == null ? void 0 : enabledWarehousesListConfig.enabledWarehousesList) == null ? void 0 : _a.some(
    (enabledWarehouseId) => enabledWarehouseId === (nearestWarehouse == null ? void 0 : nearestWarehouse.catalog)
  );
  const isBOPIWWarehouseEnabled = (_b = enabledWarehousesListConfig == null ? void 0 : enabledWarehousesListConfig.enabledWarehousesBOPIWList) == null ? void 0 : _b.some(
    (enabledWarehouseId) => enabledWarehouseId === (nearestWarehouse == null ? void 0 : nearestWarehouse.catalog)
  );
  const isWIOWarehouseEnabled = (_c = enabledWarehousesListConfig == null ? void 0 : enabledWarehousesListConfig.enabledWarehousesWIOList) == null ? void 0 : _c.some(
    (enabledWarehouseId) => enabledWarehouseId === (nearestWarehouse == null ? void 0 : nearestWarehouse.catalog)
  );
  const isBOPIMEnabled = (_d = enabledWarehousesListConfig == null ? void 0 : enabledWarehousesListConfig.enabledWarehousesBOPIMList) == null ? void 0 : _d.some(
    (enabledMDOId) => {
      var _a2;
      return enabledMDOId === ((_a2 = warehouse == null ? void 0 : warehouse.pickUpCenters) == null ? void 0 : _a2[0]);
    }
  );
  if (!isWarehouseEnabled || !(inventoryData == null ? void 0 : inventoryData.length) || !city) return null;
  const { fieldData, programTypes, attributes } = item;
  const { catalogData } = publishedChildItems || {};
  const wioProgramType = checkProgramTypeKeyExists(programTypes, ProgramTypeVariants.IN_WAREHOUSE);
  const bopiwProgramType = checkProgramTypeKeyExists(programTypes, ProgramTypeVariants.USE_WAREHOUSE_INVENTORY);
  const bopimProgramType = checkProgramTypeKeyExists(programTypes, ProgramTypeVariants.LTL_PICKUP);
  const shipToWarehouseProgramType = ((_f = (_e = attributes == null ? void 0 : attributes.find((attr) => attr.key === ATTRIBUTE_DELIVERY_TYPE)) == null ? void 0 : _e.value) == null ? void 0 : _f.toLowerCase()) === ATTRIBUTE_WAREHOUSE_PICK_UP_VALUE;
  const currentItemInventoryData = hasSingleSku && ((_h = (_g = inventoryData == null ? void 0 : inventoryData.filter(
    (inventoryItem) => {
      var _a2;
      return (inventoryItem == null ? void 0 : inventoryItem.itemNumber) === ((_a2 = catalogData == null ? void 0 : catalogData[0]) == null ? void 0 : _a2.itemNumber);
    }
  )) == null ? void 0 : _g[0]) == null ? void 0 : _h.programTypes) || {};
  const inWarehouseAvailability = (_i = currentItemInventoryData == null ? void 0 : currentItemInventoryData.inWarehouse) == null ? void 0 : _i.availability;
  const bopimAvailability = (_j = currentItemInventoryData == null ? void 0 : currentItemInventoryData.ltlpickup) == null ? void 0 : _j.availability;
  const deliveryAvailability = ((_k = currentItemInventoryData == null ? void 0 : currentItemInventoryData.locationControlledInventory) == null ? void 0 : _k.availability) || ((_l = currentItemInventoryData == null ? void 0 : currentItemInventoryData.siteControlledInventory) == null ? void 0 : _l.availability);
  if (!hasSingleSku && (fieldData == null ? void 0 : fieldData.swatchable) === 1 && showMultiVariantsAvailability && (isBOPIWWarehouseEnabled && bopiwProgramType || isWIOWarehouseEnabled && wioProgramType)) {
    return {
      status: WarehouseChannelBadgeStatus.SEE_PRODUCT_DETAILS,
      warehouse: city
    };
  } else if (hasSingleSku && isBOPIWWarehouseEnabled && bopiwProgramType && inWarehouseAvailability === StockVariants.IN_STOCK) {
    return {
      status: WarehouseChannelBadgeStatus.IN_STOCK_PICK_UP,
      warehouse: city
    };
  } else if (hasSingleSku && isWIOWarehouseEnabled && wioProgramType && inWarehouseAvailability === StockVariants.IN_STOCK) {
    return {
      status: WarehouseChannelBadgeStatus.IN_STOCK_IN_WAREHOUSE,
      warehouse: city
    };
  } else if (hasSingleSku && isWIOWarehouseEnabled && wioProgramType && inWarehouseAvailability === StockVariants.LOW_STOCK) {
    return {
      status: WarehouseChannelBadgeStatus.LOW_STOCK_IN_WAREHOUSE,
      warehouse: city
    };
  } else if (hasSingleSku && isBOPIMEnabled && bopimProgramType && bopimAvailability !== StockVariants.NO_STOCK) {
    return {
      status: WarehouseChannelBadgeStatus.IN_STOCK_MDO_PICK_UP,
      warehouse: city
    };
  } else if (hasSingleSku && shipToWarehouseProgramType && deliveryAvailability !== StockVariants.NO_STOCK) {
    return {
      status: WarehouseChannelBadgeStatus.SHIPS_TO_WAREHOUSE,
      warehouse: city
    };
  } else if (hasSingleSku && (isWIOWarehouseEnabled && wioProgramType || isBOPIWWarehouseEnabled && bopiwProgramType) && inWarehouseAvailability === StockVariants.NO_STOCK) {
    return {
      status: WarehouseChannelBadgeStatus.OUT_OF_STOCK,
      warehouse: city
    };
  }
  return null;
};
const getDeliveryStatusFromInventory = (wioWarehouseEnabled, bopiwWarehouseEnabled, inventoryData) => {
  return inventoryData == null ? void 0 : inventoryData.some((item) => {
    const { programTypes } = item;
    const locationControlledInventoryAvailable = getProgramTypeAvailability(
      (programTypes == null ? void 0 : programTypes.locationControlledInventory) || {}
    );
    const siteControlledInventoryAvailable = getProgramTypeAvailability(
      (programTypes == null ? void 0 : programTypes.siteControlledInventory) || {}
    );
    const wioWarehouseInventoryCheck = wioWarehouseEnabled && getProgramTypeAvailability((programTypes == null ? void 0 : programTypes.inWarehouse) || {});
    const bopiwWarehouseInventoryCheck = bopiwWarehouseEnabled && (getProgramTypeAvailability((programTypes == null ? void 0 : programTypes.inWarehouse) || {}) || getProgramTypeAvailability(
      (programTypes == null ? void 0 : programTypes.useWarehouseInventory) || {}
    ));
    return siteControlledInventoryAvailable || locationControlledInventoryAvailable || Boolean(wioWarehouseInventoryCheck || bopiwWarehouseInventoryCheck);
  });
};
const getDeliveryChannelBadgeData = (catalogItem, bopiwWarehouseEnabled, wioWarehouseEnabled, inventoryData) => {
  if (!inventoryData || !inventoryData.length) return null;
  const { buyable, fieldData } = catalogItem;
  const { startDate } = fieldData || {};
  const isLocationOrSiteControlled = inventoryData == null ? void 0 : inventoryData.some(
    (item) => {
      var _a, _b;
      return ((_a = item == null ? void 0 : item.programTypes) == null ? void 0 : _a.locationControlledInventory) || ((_b = item == null ? void 0 : item.programTypes) == null ? void 0 : _b.siteControlledInventory);
    }
  );
  if (isLocationOrSiteControlled && buyable === 1) {
    const currentTimeStamp = /* @__PURE__ */ new Date();
    const parsedStartDate = startDate && new Date(startDate);
    if (parsedStartDate && parsedStartDate < currentTimeStamp) {
      const availabilityStatus = getDeliveryStatusFromInventory(
        wioWarehouseEnabled,
        bopiwWarehouseEnabled,
        inventoryData
      );
      return availabilityStatus ? DeliveryStatus.Available : DeliveryStatus.OutOfStock;
    } else {
      return DeliveryStatus.ComingSoon;
    }
  } else {
    return DeliveryStatus.Unavailable;
  }
};
const bopiwOrWioInventoryCheck = (bopiWWarehouseEnabled, wioWarehouseEnabled, pType, programTypeObj) => {
  const bopiCheck = bopiWWarehouseEnabled && (pType.toLowerCase() === ProgramTypeVariants.IN_WAREHOUSE.toLowerCase() || pType.toLowerCase() === ProgramTypeVariants.USE_WAREHOUSE_INVENTORY.toLowerCase()) && getProgramTypeAvailability(
    programTypeObj[pType] || {}
  );
  const wioCheck = wioWarehouseEnabled && pType.toLowerCase() === ProgramTypeVariants.IN_WAREHOUSE.toLowerCase() && getProgramTypeAvailability(
    programTypeObj[pType] || {}
  );
  return bopiCheck || wioCheck;
};
const getProductActions = (catalogItem, featureToggleConfig, translationsConfig, hasUserSignedId, wioWarehouseEnabled, bopiwWarehouseEnabled, hasSingleSku, wcMemberValue, inventoryData, childData, isSponsored, cookieContext, isProductDetailsPage) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const {
    additionalFieldData,
    buyable,
    fieldData,
    programTypes,
    itemNumber
  } = catalogItem;
  const { tobaccoInState } = translationsConfig;
  const COMING_SOON_FEATURE_ENABLED = featureToggleConfig == null ? void 0 : featureToggleConfig.comingSoonBanner.featureOn;
  const PLP_STOCK_STATUS_DISPLAY_ENABLED = featureToggleConfig == null ? void 0 : featureToggleConfig.stockStatus.featureOn;
  const isPurchasable = buyable === 1 && ((_a = childData == null ? void 0 : childData.catalogData) == null ? void 0 : _a.some((cItem) => cItem.buyable === 1));
  const enableAddButton = isPurchasable && (childData == null ? void 0 : childData.catalogData) && (childData == null ? void 0 : childData.catalogData.length) <= 1;
  const checkInStockInventory = () => {
    if (isBD() && isSponsored) {
      return true;
    }
    if (!inventoryData) return false;
    const inventoryProgramTypesSet = /* @__PURE__ */ new Set([
      ProgramTypeVariants.TWO_DAY_DELIVERY,
      ProgramTypeVariants.LOCATION_CONTROLLED_INVENTORY,
      ProgramTypeVariants.SITE_CONTROLLED_INVENTORY
    ]);
    return inventoryData.some((item) => {
      const { programTypes: programTypes2 } = item;
      for (const pType in programTypes2) {
        if (inventoryProgramTypesSet.has(
          pType
        ) && getProgramTypeAvailability(
          programTypes2[pType] || {}
        ) || bopiwOrWioInventoryCheck(
          bopiwWarehouseEnabled,
          wioWarehouseEnabled,
          pType,
          programTypes2
        )) {
          return true;
        }
      }
      return false;
    });
  };
  const inStock = checkInStockInventory() || ((_b = childData == null ? void 0 : childData.catalogData) == null ? void 0 : _b.some((cItem) => {
    const { additionalFieldData: additionalFieldData2 } = cItem;
    const { disponZeroInv, backOrderableType, backOrderQuantity } = additionalFieldData2 || {};
    return disponZeroInv === 1 && backOrderableType && backOrderableType > 0 && backOrderQuantity && backOrderQuantity > 0;
  }));
  const currentTimeStamp = /* @__PURE__ */ new Date();
  const comingSoon = COMING_SOON_FEATURE_ENABLED && (fieldData && fieldData.startDate ? new Date(fieldData.startDate) > currentTimeStamp : false);
  !PLP_STOCK_STATUS_DISPLAY_ENABLED || getDeliveryStatusFromInventory(
    wioWarehouseEnabled,
    bopiwWarehouseEnabled,
    inventoryData
  );
  const { bdDeliveryLocation, wcMember } = cookieContext || {};
  const displayBDAddToCart = isBD() && ((bdDeliveryLocation == null ? void 0 : bdDeliveryLocation.zipCode) && (additionalFieldData == null ? void 0 : additionalFieldData.membershipReqd) === 1 && (additionalFieldData == null ? void 0 : additionalFieldData.dispPriceInCartOnly) === 0 || hasUserSignedId || hasMembershipId((wcMember == null ? void 0 : wcMember.value) || "")) && buyable && inStock;
  const displayBDSignInButton = isBD() && getMemberOnlyData(catalogItem, childData) && (!hasUserSignedId && !hasMembershipId(wcMemberValue) || "");
  const catalogAdditionalFieldData = catalogItem == null ? void 0 : catalogItem.additionalFieldData;
  const childAdditionalFieldData = (_d = (_c = childData == null ? void 0 : childData.catalogData) == null ? void 0 : _c[0]) == null ? void 0 : _d.additionalFieldData;
  const displayBDSignInForDetails = isBD() && (bdDeliveryLocation == null ? void 0 : bdDeliveryLocation.zipCode) && getMemberOnlyData(catalogItem, childData) || isBD() && buyable && hasSingleSku && ((catalogAdditionalFieldData == null ? void 0 : catalogAdditionalFieldData.productClassType) === "Tobacco" || (childAdditionalFieldData == null ? void 0 : childAdditionalFieldData.productClassType) === "Tobacco" || (catalogAdditionalFieldData == null ? void 0 : catalogAdditionalFieldData.as400catcode) === "EBB" && (catalogAdditionalFieldData == null ? void 0 : catalogAdditionalFieldData.as400deptnumber) === "11" || (childAdditionalFieldData == null ? void 0 : childAdditionalFieldData.as400catcode) === "EBB" && (childAdditionalFieldData == null ? void 0 : childAdditionalFieldData.as400deptnumber) === "11") && ((_e = bdDeliveryLocation == null ? void 0 : bdDeliveryLocation.bdLocation) == null ? void 0 : _e.includes(
    tobaccoInState == null ? void 0 : tobaccoInState.split("|")[1]
  ));
  if (isProductDetailsPage && !hasActiveMembership((wcMember == null ? void 0 : wcMember.value) || "", hasUserSignedId)) {
    return {
      type: (((_f = childData == null ? void 0 : childData.catalogData) == null ? void 0 : _f.length) ?? 0) > 1 ? "SelectOptions" : "SeeDetails"
    };
  }
  if (getMemberOnlyData(catalogItem, childData) && !hasUserSignedId || displayBDSignInButton || displayBDSignInForDetails) {
    return { type: "SignInButton" };
  } else if (((additionalFieldData == null ? void 0 : additionalFieldData.dispPriceInCartOnly) === 0 || hasUserSignedId && getMemberOnlyData(catalogItem, childData) && hasMembershipId((wcMember == null ? void 0 : wcMember.value) || "") || hasUserSignedId && !getMemberOnlyData(catalogItem, childData)) && enableAddButton && inStock && !comingSoon || displayBDAddToCart) {
    return {
      contractIdentifier: checkProgramTypeKeyExists(
        programTypes || "",
        ProgramTypeVariants.TWO_DAY_DELIVERY
      ) ? "Regional" : void 0,
      itemNumber: (_h = (_g = childData == null ? void 0 : childData.catalogData) == null ? void 0 : _g[0]) == null ? void 0 : _h.itemNumber,
      maxQuantity: additionalFieldData == null ? void 0 : additionalFieldData.maxItemOrderQty,
      minQuantity: additionalFieldData == null ? void 0 : additionalFieldData.minItemOrderQty,
      partNumber: itemNumber,
      type: "QuantitySelector"
    };
  } else if (isBD() && !inStock && isPurchasable) {
    return { type: "OutOfStock" };
  } else if (isProductDetailsPage && isPurchasable) {
    return {
      type: (((_i = childData == null ? void 0 : childData.catalogData) == null ? void 0 : _i.length) ?? 0) > 1 ? "SelectOptions" : "SeeDetails"
    };
  } else if (!inStock && isPurchasable) {
    return { type: "SeeDetails" };
  } else {
    return { type: "SelectOptions" };
  }
};
export {
  getAddToListButtonData,
  getChdiEligibility,
  getColorSwatchesData,
  getCompareProductButtonData,
  getDeliveryChannelBadgeData,
  getDiscountsData,
  getFsaEligibility,
  getImageUrl,
  getInWareHouseChannelBadgeData,
  getInWarehouseAvailabilityText,
  getMemberOnlyData,
  getPillBadges,
  getPriceData,
  getPriceList,
  getProductActions,
  getProductDeliveryBadgeData,
  getProductFeaturesData,
  getProductNavigationUrl,
  getProgramTypeAvailability,
  getReplacementItemNumber,
  getReviewRatingData,
  getTotalDiscount
};
